import * as logicUser from "@logic/user";
import Button from '@components/button';
import Icon from '@components/icon';
import React, { useState, useCallback, useEffect, useRef } from "react";
import TextInput from '@components/textInput';
import { checkConstant, ACCOUNT_TYPE } from "@logic/constants";
import { selectUser } from '@store/userSlice';
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useSnackbars } from "@contexts/SnackbarsContext";
import styles from "./PersonalInfo.module.css";

const PersonallInfo = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { openSnackbar } = useSnackbars();

  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState({});
  const [businessLogo, setBusinessLogo] = useState(null);
  const [businessLogoPreview, setBusinessLogoPreview] = useState(null);
  const businessLogoInputRef = useRef(null);

  const onFormValueChange = useCallback((fieldName, newValue) => {
    setValues((prev) => ({
      ...prev,
      [fieldName]: newValue,
    }));
  }, []);

  const onSave = useCallback(() => {
    setLoading(true);
    const payload = {
      ...values,
      userId: user.id,
    };
    if (businessLogo) {
      payload.businessLogo = businessLogo;
    }
    dispatch(logicUser.editUserPublicData(payload))
      .then(() => {
        openSnackbar("Dati aggiornati ✔️");
      })
      .catch((e) => {
        openSnackbar("Qualcosa è andato storto ❌");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [businessLogo, dispatch, openSnackbar, user.id, values]);

  useEffect(() => {
    setValues({
      advertisementName: user.advertisementName ?? "",
      cel: user.cel ?? "",
      cel2: user.cel2 ?? "",
      tel: user.tel ?? "",
      displayEmail: user.displayEmail ?? "",
      address: user.address ?? "",
      website: user.website ?? "",
      facebook: user.facebook ?? "",
      instagram: user.instagram ?? "",
    });
    setBusinessLogo(null);
    setBusinessLogoPreview(null);
  }, [user]);

  const isPrivato = checkConstant(ACCOUNT_TYPE.PRIVATO, user.accountType);
  const isAzienda = checkConstant(ACCOUNT_TYPE.AZIENDA, user.accountType);

  useEffect(() => {
    if (!isAzienda || !user?.id) {
      setBusinessLogoPreview(null);
      return;
    }

    if (businessLogo) {
      return;
    }

    const logoUrl = logicUser.getBusinessLogoUrl({
      userId: user.id
    });
    setBusinessLogoPreview(logoUrl);
  }, [businessLogo, isAzienda, user?.id]);

  const onBusinessLogoChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (fileEvent) => {
      const result = fileEvent.target?.result;
      if (typeof result === "string") {
        setBusinessLogo(result);
        setBusinessLogoPreview(result);
      }
    };
    reader.onerror = () => {
      openSnackbar("Non è stato possibile caricare l'immagine ❌");
    };
    reader.readAsDataURL(file);
    event.target.value = null;
  }, [openSnackbar]);

  const onSelectBusinessLogo = useCallback(() => {
    businessLogoInputRef.current?.click();
  }, []);

  return (
    <>
      <br></br>
      <div className='row'>
        <div className='col'>
          <span className='page-title'>I tuoi dati utente pubblici</span>
        </div>
      </div>
      {isPrivato && (
        <>
          <div className='row'>
            <div className='col'>
              <TextInput label="Username" value={values.advertisementName ?? ""} setValue={(val) => onFormValueChange("advertisementName", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Cellulare" value={values.cel ?? ""} setValue={(val) => onFormValueChange("cel", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Telefono" value={values.tel ?? ""} setValue={(val) => onFormValueChange("tel", val)} disabled={loading} />
            </div>
          </div>
        </>
      )}
      {isAzienda && (
        <>
          <div className='row'>
            <div className='col'>
              <div className={styles.businessLogoSection}>
                <span className={styles.businessLogoLabel}>Logo aziendale</span>
                <div className={styles.businessLogoPreview}>
                  {businessLogoPreview ? (
                    <img
                      src={businessLogoPreview}
                      alt={`Logo ${values.advertisementName || "azienda"}`}
                      className={styles.businessLogoImage}
                    />
                  ) : (
                    <Icon
                      name="storefront"
                      size={48}
                      fill={1}
                      weight={400}
                      className={styles.businessLogoPlaceholderIcon}
                    />
                  )}
                </div>
                <input
                  ref={businessLogoInputRef}
                  type='file'
                  accept='image/*'
                  onChange={onBusinessLogoChange}
                  style={{ display: "none" }}
                  disabled={loading}
                />
                <Button
                  icon="upload"
                  text="Carica logo"
                  onClick={onSelectBusinessLogo}
                  disabled={loading}
                />
              </div>
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Nome azienda" value={values.advertisementName ?? ""} setValue={(val) => onFormValueChange("advertisementName", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Indirizzo" value={values.address ?? ""} setValue={(val) => onFormValueChange("address", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Cellulare" value={values.cel ?? ""} setValue={(val) => onFormValueChange("cel", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Cellulare 2" value={values.cel2 ?? ""} setValue={(val) => onFormValueChange("cel2", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Telefono" value={values.tel ?? ""} setValue={(val) => onFormValueChange("tel", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Email pubblica" value={values.displayEmail ?? ""} setValue={(val) => onFormValueChange("displayEmail", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Sito internet" value={values.website ?? ""} setValue={(val) => onFormValueChange("website", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Facebook" value={values.facebook ?? ""} setValue={(val) => onFormValueChange("facebook", val)} disabled={loading} />
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <TextInput label="Instagram" value={values.instagram ?? ""} setValue={(val) => onFormValueChange("instagram", val)} disabled={loading} />
            </div>
          </div>
        </>
      )}
      
      <br></br>
      <div className='row'>
        <div className='col col-flex-center'>
          <Button
            color="primary"
            disabled={loading}
            // fullWidth
            // icon="save"
            onClick={onSave}
            text="Salva"
          />
        </div>
      </div>
    </>
  );
};

export default PersonallInfo;
